import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { updateOnboarding } from '../../clients/onboardingClient';
import type { Step, StepRuntime } from '../types/types';
import { StepSlug } from '../types/types';

function RoleStep({ next, saveStep, context }: StepRuntime): JSX.Element {
  const [selected, setSelected] = useState<string | null>(null);

  const ROLE_OPTIONS: Record<string, string[]> = {
    'Social Services': ['Social Worker', 'Case Manager', 'Counselor', 'Therapist', 'Other'],
    Healthcare: ['Nurse', 'Doctor', 'Medical Assistant', 'Support Staff', 'Other'],
    Finance: ['Advisor', 'Planner', 'Coach', 'Accountant', 'Other'],
    Education: ['Teacher', 'Student Services', 'Administrator', 'Counselor', 'Researcher', 'Other'],
    Legal: ['Lawyer', 'Paralegal', 'Compliance Officer', 'Other'],
    Nonprofit: ['Program Manager', 'Volunteer Coordinator', 'Director', 'Other'],
    Operations: ['Administrator', 'HR', 'Project Manager', 'Other'],
    Research: ['Researcher', 'Analyst', 'Data Scientist', 'Other'],
    Other: ['Other']
  };

  // Get the industry from the previous step's answers
  const sector = (context.answers[StepSlug.Industry] as string) || 'Other';
  const options = ROLE_OPTIONS[sector] ?? ROLE_OPTIONS['Other'];

  const onboardingMutation = useMutation({
    mutationFn: updateOnboarding,
    onSettled: () => {
      if (selected) {
        saveStep(StepSlug.Role, selected);
        // Add a small delay before advancing to the next page
        setTimeout(() => {
          next(StepSlug.RecordingOptions);
        }, 200); // 0.20 second delay
      }
    }
  });

  function submit(role: string) {
    // Calculate progress based on current step (Role is step 2 of 4)
    const progress = 1;

    onboardingMutation.mutate({
      role: role,
      progress
    });
  }

  function handleSelection(option: string) {
    setSelected(option);
    // Automatically submit when an option is selected
    submit(option);
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-[url('./images/grid.png')] bg-repeat bg-[length:500px] px-6 relative">
      {/* Subheading placed a quarter down, same treatment as page 1 */}
      <div className="absolute top-1/4 w-full text-center">
        <h2 className="text-lg font-medium text-indigo-800/60">
          Let's tailor <span className="text-indigo-800">Notewell</span> to your needs ✏️
        </h2>
      </div>

      <div className="max-w-2xl text-center">
        <h1 className="text-3xl font-semibold text-gray-900 mb-10">What's your role?</h1>

        <div className="flex flex-wrap justify-center gap-3">
          {options.map((option) => (
            <button
              key={option}
              onClick={() => handleSelection(option)}
              className={`px-5 py-2 rounded-full border text-base transition-colors duration-200
                ${
                  selected === option
                    ? 'bg-indigo-600 text-white border-indigo-600'
                    : 'bg-indigo-50 text-indigo-700 border-indigo-300 hover:border-indigo-700 hover:text-indigo-600 hover:bg-indigo-200'
                }`}
            >
              {option}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

export const RoleStepModule: Step = {
  slug: StepSlug.Role,
  title: 'Role',
  Component: RoleStep
};
